import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { currentLocale } from '../../i18n'
import { toISODate } from '../../utils/weeks'
import { ZOOMS } from './ganttScale'

// Widths of the frozen block on the left, mirroring the reference workbook's
// "# | Tarea | Inicio | Fin | Dias hab." columns.
const NUM_W = 34
const NAME_W = 250
const DATE_W = 78
const DAYS_W = 54
const LABEL_W = NUM_W + NAME_W + DATE_W * 2 + DAYS_W

const ROW_H = 26
const PHASE_H = 24
const HDR_H = 54          // month band + week label + day number

function monthLabel(year, month) {
  return new Date(year, month - 1, 1)
    .toLocaleDateString(currentLocale(), { month: 'short', year: 'numeric' })
}

function dayLabel(iso) {
  return new Date(iso + 'T00:00:00').toLocaleDateString(currentLocale(), {
    day: 'numeric', month: 'short',
  })
}

function shortDate(iso) {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00')
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** Darken a #RRGGBB by a factor, for the progress fill inside a bar. */
function darken(hex, factor = 0.72) {
  if (!/^#[0-9a-f]{6}$/i.test(hex || '')) return hex
  const n = parseInt(hex.slice(1), 16)
  const parts = [(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map(c => Math.max(0, Math.round(c * factor)))
  return `#${parts.map(c => c.toString(16).padStart(2, '0')).join('')}`
}

export default function GanttChart({
  data, zoom = 'normal', fullscreen = false, scrollRef,
  collapsed = {}, onTogglePhase,
}) {
  const { t } = useTranslation('engineering')
  const internalRef = useRef(null)
  const ref = scrollRef || internalRef
  const dayW = ZOOMS[zoom] || ZOOMS.normal

  const days = useMemo(() => data?.days || [], [data])
  const totalW = days.length * dayW

  // Today's column is resolved here, not on the server: a UTC backend and a
  // Chilean reader disagree about the date for a few hours every night, and
  // the red line should follow the person looking at it.
  const todayIndex = useMemo(() => {
    const iso = toISODate(new Date())
    const i = days.indexOf(iso)
    return i === -1 ? null : i
  }, [days])

  const holidayCols = useMemo(() => {
    const index = new Map(days.map((iso, i) => [iso, i]))
    return Object.entries(data?.holidays || {})
      .map(([iso, name]) => ({ index: index.get(iso), iso, name }))
      .filter(h => h.index !== undefined)
  }, [days, data])

  // Scroll to today on first paint, so a long plan opens where work is.
  useEffect(() => {
    if (todayIndex == null || !ref.current) return
    ref.current.scrollLeft = Math.max(0, todayIndex * dayW - 220)
  }, [todayIndex, dayW, ref])

  const rowCount = (data?.phases || []).reduce((n, p) => n + p.rows.length, 0)
  if (!days.length || rowCount === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 py-14 text-center">
        <p className="text-sm text-gray-400">{t('projectDetail.gantt.noData')}</p>
      </div>
    )
  }

  // One repeating gradient instead of a cell per day per row: a 400-column
  // chart with 50 rows would otherwise be 20 000 DOM nodes.
  const gridBackground = {
    backgroundImage: [
      `repeating-linear-gradient(to right, transparent 0 ${dayW - 1}px, #f1f2f4 ${dayW - 1}px ${dayW}px)`,
      `repeating-linear-gradient(to right, transparent 0 ${dayW * 5 - 1}px, #e2e5e9 ${dayW * 5 - 1}px ${dayW * 5}px)`,
    ].join(','),
  }

  const stickyCell = 'sticky bg-white'
  const headStyle = { height: HDR_H }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div
        ref={ref}
        className="overflow-auto"
        style={{ maxHeight: fullscreen ? 'calc(100vh - 150px)' : '70vh' }}
      >
        <div style={{ width: LABEL_W + totalW, position: 'relative' }}>

          {/* ── Header ─────────────────────────────────────────────── */}
          <div className="flex sticky top-0 z-50" style={headStyle}>
            {/* Frozen corner: above both the header row and the label column */}
            <div
              className={`${stickyCell} left-0 z-[60] flex items-end border-b border-r border-gray-200`}
              style={{ width: LABEL_W, minWidth: LABEL_W, ...headStyle }}
            >
              <div className="flex w-full text-[10px] font-semibold text-gray-500 uppercase tracking-wide pb-1">
                <span style={{ width: NUM_W }} className="text-center">{t('projectDetail.gantt.numHeader')}</span>
                <span style={{ width: NAME_W }} className="px-1">{t('projectDetail.gantt.nameHeader')}</span>
                <span style={{ width: DATE_W }} className="text-center">{t('projectDetail.gantt.startHeader')}</span>
                <span style={{ width: DATE_W }} className="text-center">{t('projectDetail.gantt.endHeader')}</span>
                <span style={{ width: DAYS_W }} className="text-center">{t('projectDetail.gantt.businessDaysHeader')}</span>
              </div>
            </div>

            {/* Month band, week labels, day numbers */}
            <div className="relative bg-white border-b border-gray-200"
                 style={{ width: totalW, minWidth: totalW, ...headStyle }}>
              {data.months.map(band => (
                <div
                  key={`${band.year}-${band.month}`}
                  className="absolute top-0 text-[10px] font-semibold text-gray-600 uppercase border-l border-gray-200 bg-gray-50 truncate px-1"
                  style={{ left: band.index * dayW, width: band.span * dayW, height: 18, lineHeight: '18px' }}
                >
                  {monthLabel(band.year, band.month)}
                </div>
              ))}
              {data.weeks.map(week => (
                <div
                  key={week.index}
                  className="absolute text-[9px] text-gray-400 border-l border-gray-200 pl-0.5"
                  style={{ left: week.index * dayW, top: 18, width: dayW * 5, height: 16, lineHeight: '16px' }}
                >
                  {dayW >= 12 ? shortDate(week.monday) : ''}
                </div>
              ))}
              {/* Day numbers are the first thing to go when columns get thin. */}
              {dayW >= 14 && days.map((iso, i) => (
                <div
                  key={iso}
                  className="absolute text-[9px] text-gray-400 text-center"
                  style={{ left: i * dayW, top: 34, width: dayW, height: 18, lineHeight: '18px' }}
                >
                  {new Date(iso + 'T00:00:00').getDate()}
                </div>
              ))}
            </div>
          </div>

          {/* ── Body ───────────────────────────────────────────────── */}
          <div className="relative">
            {/* Holiday columns and the today line span every row, so they are
                drawn once over the whole body rather than per row. The grey
                sits ABOVE the bars, exactly as it does in the workbook. */}
            <div className="absolute inset-0 pointer-events-none z-20">
              {holidayCols.map(h => (
                <div
                  key={h.iso}
                  title={`${t('projectDetail.gantt.holiday')}: ${h.name} — ${dayLabel(h.iso)}`}
                  className="absolute top-0 bottom-0 pointer-events-auto"
                  style={{ left: LABEL_W + h.index * dayW, width: dayW, background: '#D9D9D9', opacity: 0.85 }}
                />
              ))}
              {todayIndex != null && (
                <div
                  className="absolute top-0 bottom-0"
                  style={{ left: LABEL_W + todayIndex * dayW, width: 2, background: '#ef4444', opacity: 0.75 }}
                />
              )}
            </div>

            {data.phases.map((phase, phaseIndex) => {
              const key = phase.id ?? `ungrouped-${phaseIndex}`
              const isCollapsed = !!collapsed[key]
              const sum = phase.summary || {}
              const sumDrawn = sum.col_start != null
              const sumLeft = sumDrawn ? sum.col_start * dayW : 0
              const sumWidth = sumDrawn ? (sum.col_end - sum.col_start + 1) * dayW : 0
              const sumColor = sum.at_risk ? '#C00000' : phase.color
              const hidden = phase.rows.length
              const sumTip = [
                phase.name || t('projectDetail.gantt.ungrouped'),
                sum.start ? `${sum.start} → ${sum.end}` : t('projectDetail.gantt.noDates'),
                sum.business_days != null
                  ? `${t('projectDetail.gantt.businessDaysHeader')} ${sum.business_days}` : '',
                t('projectDetail.gantt.phaseRollup', {
                  done: sum.done_count ?? 0, tasks: sum.task_count ?? 0,
                  milestones: sum.milestone_count ?? 0,
                }),
              ].filter(Boolean).join('\n')

              return (
              <div key={key}>
                {/* Phase header band — the whole row toggles the phase */}
                <div className="flex" style={{ height: PHASE_H }}>
                  <button
                    type="button"
                    onClick={() => onTogglePhase?.(key)}
                    aria-expanded={!isCollapsed}
                    title={t(isCollapsed ? 'projectDetail.gantt.expandPhase'
                                         : 'projectDetail.gantt.collapsePhase')}
                    className={`${stickyCell} left-0 z-30 flex items-center border-b border-r border-gray-200 text-left hover:bg-gray-200/70 cursor-pointer`}
                    style={{ width: LABEL_W, minWidth: LABEL_W, background: '#f3f4f6' }}
                  >
                    <span className="shrink-0 text-gray-400 text-[9px] text-center"
                          style={{ width: NUM_W }}>
                      {isCollapsed ? '▶' : '▼'}
                    </span>
                    <span className="flex items-center gap-2 min-w-0"
                          style={{ width: NAME_W }}>
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0"
                            style={{ background: phase.color }} />
                      <span className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide truncate"
                            title={phase.name || t('projectDetail.gantt.ungrouped')}>
                        {phase.name || t('projectDetail.gantt.ungrouped')}
                      </span>
                      {isCollapsed && (
                        <span className="shrink-0 text-[10px] font-normal text-gray-400 normal-case">
                          {t('projectDetail.gantt.rowsCount', { count: hidden })}
                        </span>
                      )}
                    </span>
                    {/* The phase's own span, on the same rule as any row */}
                    <span style={{ width: DATE_W }}
                          className="text-center text-[11px] text-gray-500 tabular-nums">
                      {shortDate(sum.start)}
                    </span>
                    <span style={{ width: DATE_W }}
                          className="text-center text-[11px] text-gray-500 tabular-nums">
                      {shortDate(sum.end)}
                    </span>
                    <span style={{ width: DAYS_W }}
                          className="text-center text-[11px] text-gray-500 tabular-nums">
                      {sum.business_days ?? ''}
                    </span>
                  </button>
                  <div style={{ width: totalW, minWidth: totalW, background: '#f3f4f6' }}
                       className="relative border-b border-gray-200">
                    {/* Collapsed, the phase IS the bar. Expanded, a slim
                        bracket still shows where the phase begins and ends. */}
                    {sumDrawn && (
                      <div
                        title={sumTip}
                        className="absolute overflow-hidden"
                        style={{
                          left: sumLeft, width: Math.max(sumWidth, 3),
                          top: isCollapsed ? 4 : PHASE_H / 2 - 2,
                          height: isCollapsed ? PHASE_H - 8 : 4,
                          borderRadius: isCollapsed ? 3 : 2,
                          background: sumColor,
                          border: isCollapsed ? `1px solid ${darken(sumColor, 0.75)}` : 'none',
                          opacity: isCollapsed ? 1 : 0.5,
                        }}
                      >
                        {isCollapsed && sum.progress > 0 && (
                          <div className="absolute left-0 top-0 bottom-0"
                               style={{ width: `${Math.min(sum.progress, 100)}%`,
                                        background: darken(sumColor, 0.62) }} />
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {!isCollapsed && phase.rows.map((row, i) => {
                  const stripe = i % 2 === 1 ? '#fafafa' : '#ffffff'
                  const drawn = row.col_start != null
                  const barLeft = drawn ? row.col_start * dayW : 0
                  const barWidth = drawn ? (row.col_end - row.col_start + 1) * dayW : 0
                  const tip = [
                    row.name,
                    row.start ? `${row.start} → ${row.end}` : t('projectDetail.gantt.noDates'),
                    row.business_days != null
                      ? `${t('projectDetail.gantt.businessDaysHeader')} ${row.business_days}` : '',
                    row.responsible_name
                      ? `${t('projectDetail.gantt.responsibleLabel')}: ${row.responsible_name}` : '',
                    row.at_risk ? t('projectDetail.gantt.atRisk') : '',
                  ].filter(Boolean).join('\n')

                  return (
                    <div key={`${row.kind}-${row.id}`} className="flex" style={{ height: ROW_H }}>
                      {/* Frozen label block */}
                      <div
                        className={`${stickyCell} left-0 z-30 flex items-center border-b border-r border-gray-100 text-[11px]`}
                        style={{ width: LABEL_W, minWidth: LABEL_W, background: stripe }}
                      >
                        <span style={{ width: NUM_W }} className="text-center text-gray-400 tabular-nums">
                          {row.ordinal}
                        </span>
                        <span style={{ width: NAME_W }} className="px-1 flex items-center gap-1 min-w-0">
                          {row.kind === 'milestone'
                            ? <span className="shrink-0 text-[9px]" style={{ color: row.color }}>◆</span>
                            : <span className="shrink-0 w-2 h-2 rounded-sm" style={{ background: row.color }} />}
                          <span className={`truncate ${row.at_risk ? 'text-red-700 font-medium' : 'text-gray-700'}`}
                                title={row.name}>
                            {row.name}
                          </span>
                        </span>
                        <span style={{ width: DATE_W }} className="text-center text-gray-500 tabular-nums">
                          {shortDate(row.start)}
                        </span>
                        <span style={{ width: DATE_W }} className="text-center text-gray-500 tabular-nums">
                          {shortDate(row.end)}
                        </span>
                        <span style={{ width: DAYS_W }} className="text-center text-gray-500 tabular-nums">
                          {row.business_days ?? ''}
                        </span>
                      </div>

                      {/* Timeline track */}
                      <div
                        className="relative border-b border-gray-100"
                        style={{ width: totalW, minWidth: totalW, background: stripe, ...gridBackground }}
                      >
                        {drawn && row.kind === 'milestone' && (
                          <div
                            title={tip}
                            className="absolute"
                            style={{
                              left: barLeft + dayW / 2 - 6, top: ROW_H / 2 - 6,
                              width: 12, height: 12, transform: 'rotate(45deg)',
                              borderRadius: 2, background: row.color,
                              border: `1.5px solid ${darken(row.color, 0.7)}`,
                            }}
                          />
                        )}
                        {drawn && row.kind === 'task' && (
                          <div
                            title={tip}
                            className="absolute overflow-hidden"
                            style={{
                              left: barLeft, width: Math.max(barWidth, 3), top: 5,
                              height: ROW_H - 11, borderRadius: 3,
                              background: row.color,
                              border: `1px solid ${darken(row.color, 0.75)}`,
                            }}
                          >
                            {row.progress > 0 && (
                              <div
                                className="absolute left-0 top-0 bottom-0"
                                style={{ width: `${Math.min(row.progress, 100)}%`,
                                         background: darken(row.color, 0.62) }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
              )
            })}
          </div>
        </div>
      </div>

      <GanttLegend phases={data.phases} />
    </div>
  )
}

function Swatch({ color, label, diamond = false }) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        className={`inline-block w-3 h-3 ${diamond ? 'rotate-45' : ''} rounded-sm border border-black/10`}
        style={{ background: color }}
      />
      {label}
    </span>
  )
}

function GanttLegend({ phases }) {
  const { t } = useTranslation('engineering')
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2 border-t border-gray-100 text-[11px] text-gray-500">
      {phases.filter(p => p.name).map(p => (
        <Swatch key={p.id} color={p.color} label={p.name} />
      ))}
      <span className="text-gray-300">|</span>
      <Swatch color="#A9D08E" label={t('projectDetail.gantt.done')} />
      <Swatch color="#C00000" label={t('projectDetail.gantt.atRisk')} />
      <Swatch color="#D9D9D9" label={t('projectDetail.gantt.holiday')} />
      <Swatch color="#806000" label={t('projectDetail.gantt.milestone')} diamond />
      <span className="flex items-center gap-1.5">
        <span className="inline-block w-0.5 h-3 bg-red-400" />
        {t('projectDetail.gantt.today')}
      </span>
    </div>
  )
}
